import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { organizationsService } from '../services/organizations.service';
import { Organization, OrganizationType } from '../types';
import { toast } from 'react-toastify';

interface OrganizationFormData {
  name: string;
  code?: string;
  contactEmail: string;
  contactPhone?: string;
  address?: string;
  type: OrganizationType;
  parentOrganizationId?: string;
  subscriptionPlan: string;
  isActive?: boolean;
  // SMS Configuration
  smsEnabled?: boolean;
  smsUsercode?: string;
  smsPassword?: string;
  smsMsgheader?: string;
  // Email Configuration
  emailEnabled?: boolean;
  emailProvider?: string;
  emailApiKey?: string;
  emailFromEmail?: string;
  emailFromName?: string;
  emailAwsRegion?: string;
  emailAwsAccessKeyId?: string;
  emailAwsSecretAccessKey?: string;
  // Meet & Greet Fee
  meetGreetFee?: number;
  guestEmailSenderName?: string;
  guestEmailMeetingPoint?: string;
}

const inputClass = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500";
const labelClass = "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1";
const helpClass = "mt-1 text-sm text-gray-500 dark:text-gray-400";
const cardClass = "bg-white dark:bg-gray-800 shadow sm:rounded-lg p-4 sm:p-6";

interface OrgStats {
  userCount: number;
  transferCount: number;
  activeTransferCount: number;
}

const OrganizationFormPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(isEditMode);
  const [allOrganizations, setAllOrganizations] = useState<Organization[]>([]);
  const [orgStats, setOrgStats] = useState<OrgStats | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<OrganizationFormData>({
    defaultValues: {
      type: OrganizationType.REGULAR,
      subscriptionPlan: 'basic',
      isActive: true,
      smsEnabled: false,
      emailEnabled: false,
      emailProvider: 'resend',
    },
  });

  const smsEnabled = watch('smsEnabled');
  const emailEnabled = watch('emailEnabled');
  const emailProvider = watch('emailProvider');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Load all organizations for parent selection
        const allOrgs = await organizationsService.getAll();
        setAllOrganizations(allOrgs);

        // If editing, load the organization data + stats
        if (id) {
          const [org, stats] = await Promise.all([
            organizationsService.getById(id),
            organizationsService.getOrgStats(id).catch(() => null),
          ]);
          setOrgStats(stats);
          reset({
            name: org.name,
            code: org.code || '',
            contactEmail: org.contactEmail,
            contactPhone: org.contactPhone || '',
            address: org.address || '',
            type: org.type,
            parentOrganizationId: org.parentOrganizationId || '',
            subscriptionPlan: org.subscriptionPlan,
            isActive: org.isActive,
            smsEnabled: org.smsEnabled || false,
            smsUsercode: org.smsUsercode || '',
            smsPassword: org.smsPassword || '',
            smsMsgheader: org.smsMsgheader || '',
            emailEnabled: org.emailEnabled || false,
            emailProvider: org.emailProvider || 'resend',
            emailApiKey: org.emailApiKey || '',
            emailFromEmail: org.emailFromEmail || '',
            emailFromName: org.emailFromName || '',
            emailAwsRegion: org.emailAwsRegion || '',
            emailAwsAccessKeyId: org.emailAwsAccessKeyId || '',
            emailAwsSecretAccessKey: org.emailAwsSecretAccessKey || '',
            meetGreetFee: org.meetGreetFee || undefined,
            guestEmailSenderName: org.guestEmailSenderName || '',
            guestEmailMeetingPoint: org.guestEmailMeetingPoint || '',
          });
        }
      } catch (error: any) {
        console.error('Failed to load data:', error);
        toast.error('Failed to load organization data');
        navigate('/app/organizations');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, navigate, reset]);

  const onSubmit = async (data: OrganizationFormData) => {
    try {
      // Clean up empty strings
      const cleanedData = {
        ...data,
        code: data.code || undefined,
        contactPhone: data.contactPhone || undefined,
        address: data.address || undefined,
        parentOrganizationId: data.parentOrganizationId || undefined,
        smsUsercode: data.smsUsercode || undefined,
        smsPassword: data.smsPassword || undefined,
        smsMsgheader: data.smsMsgheader || undefined,
        emailProvider: data.emailProvider || undefined,
        emailApiKey: data.emailApiKey || undefined,
        emailFromEmail: data.emailFromEmail || undefined,
        emailFromName: data.emailFromName || undefined,
        emailAwsRegion: data.emailAwsRegion || undefined,
        emailAwsAccessKeyId: data.emailAwsAccessKeyId || undefined,
        emailAwsSecretAccessKey: data.emailAwsSecretAccessKey || undefined,
        guestEmailSenderName: data.guestEmailSenderName || undefined,
        guestEmailMeetingPoint: data.guestEmailMeetingPoint || undefined,
      };

      console.log('Submitting organization data:', cleanedData);

      if (isEditMode && id) {
        await organizationsService.update(id, cleanedData);
        toast.success('Organizasyon güncellendi');
      } else {
        await organizationsService.create(cleanedData);
        toast.success('Organizasyon oluşturuldu');
      }

      navigate('/app/organizations');
    } catch (error: any) {
      console.error('Failed to save organization:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error messages:', JSON.stringify(error.response?.data?.message, null, 2));
      const errorMessage = Array.isArray(error.response?.data?.message)
        ? error.response.data.message.join(', ')
        : error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to save organization';
      toast.error(errorMessage);
    }
  };

  // Filter out the current organization and its descendants from parent options
  const availableParents = allOrganizations.filter((org) => {
    if (isEditMode && id) {
      // Can't be its own parent
      if (org.id === id) return false;

      // Can't select a child as parent (prevent circular reference)
      if (org.parentOrganizationId === id) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
          {isEditMode ? 'Organizasyon Düzenle' : 'Yeni Organizasyon'}
        </h1>

      </div>

      {/* Stats özeti — sadece edit modunda */}
      {isEditMode && orgStats && (
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 text-center">
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{orgStats.userCount}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Kullanıcı</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 text-center">
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{orgStats.transferCount}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Toplam Transfer</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 text-center">
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{orgStats.activeTransferCount}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Aktif Transfer</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
        <div className={cardClass}>
          <div className="space-y-4 sm:space-y-6">
            {/* Organization Name */}
            <div>
              <label className={labelClass}>
                Organizasyon Adı *
              </label>
              <input
                type="text"
                {...register('name', { required: 'Name is required' })}
                className={inputClass}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Organization Code (for Transfer ID) */}
            <div>
              <label className={labelClass}>
                Organizasyon Kodu
              </label>
              <input
                type="text"
                {...register('code', {
                  maxLength: {
                    value: 10,
                    message: 'Code must be max 10 characters',
                  },
                  pattern: {
                    value: /^[A-Z0-9]*$/,
                    message: 'Code must be uppercase letters and numbers only',
                  },
                })}
                placeholder="e.g., HAN, ABC"
                className={`${inputClass} uppercase`}
                style={{ textTransform: 'uppercase' }}
              />
              {errors.code && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.code.message}
                </p>
              )}
              <p className={helpClass}>
                Transfer ID'lerinde kullanılır (örn. HAN-26-0001). Maks 10 karakter, büyük harf.
              </p>
            </div>

            {/* Contact Email */}
            <div>
              <label className={labelClass}>
                İletişim E-postası *
              </label>
              <input
                type="email"
                {...register('contactEmail', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                className={inputClass}
              />
              {errors.contactEmail && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.contactEmail.message}
                </p>
              )}
            </div>

            {/* Contact Phone */}
            <div>
              <label className={labelClass}>
                İletişim Telefonu
              </label>
              <input
                type="tel"
                {...register('contactPhone')}
                className={inputClass}
              />
            </div>

            {/* Address */}
            <div>
              <label className={labelClass}>
                Adres
              </label>
              <textarea
                {...register('address')}
                rows={3}
                className={inputClass}
              />
            </div>

            {/* Organization Type */}
            <div>
              <label className={labelClass}>
                Organizasyon Tipi *
              </label>
              <select
                {...register('type', { required: 'Type is required' })}
                className={inputClass}
              >
                <option value={OrganizationType.REGULAR}>Regular</option>
              </select>
              {errors.type && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.type.message}
                </p>
              )}
            </div>

            {/* Parent Organization */}
            <div>
              <label className={labelClass}>
                Üst Organizasyon
              </label>
              <select
                {...register('parentOrganizationId')}
                className={inputClass}
              >
                <option value="">Yok (En üst seviye)</option>
                {availableParents.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
              <p className={helpClass}>
                Hiyerarşi oluşturmak için üst organizasyon seçin
              </p>
            </div>

            {/* Subscription Plan */}
            <div>
              <label className={labelClass}>
                Abonelik Planı *
              </label>
              <select
                {...register('subscriptionPlan', {
                  required: 'Subscription plan is required',
                })}
                className={inputClass}
              >
                <option value="basic">Basic</option>
                <option value="standard">Standard</option>
                <option value="premium">Premium</option>
                <option value="enterprise">Enterprise</option>
              </select>
              {errors.subscriptionPlan && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.subscriptionPlan.message}
                </p>
              )}
            </div>

            {/* Active Status (only show in edit mode) */}
            {isEditMode && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900 dark:text-white">
                  Aktif
                </label>
              </div>
            )}

            {/* Meet & Greet Fee */}
            <div>
              <label className={labelClass}>
                {t('organizations.meetGreetFee')}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-gray-500 dark:text-gray-400">&#8378;</span>
                <input
                  type="number"
                  step="0.01"
                  {...register('meetGreetFee', { valueAsNumber: true })}
                  className={`${inputClass} pl-8`}
                  placeholder="0.00"
                />
              </div>
              <p className={helpClass}>
                {t('organizations.meetGreetFeeHelp')}
              </p>
            </div>
          </div>
        </div>

        {/* SMS Configuration */}
        <div className={cardClass}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('organizations.smsConfig')}</h2>
            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('smsEnabled')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900 dark:text-white">{t('organizations.smsEnabled')}</label>
            </div>
          </div>
          <p className={helpClass}>{t('organizations.smsConfigHelp')}</p>
          {smsEnabled && (
            <div className="space-y-4 mt-4">
              <div>
                <label className={labelClass}>
                  {t('organizations.smsUsercode')}
                </label>
                <input
                  type="text"
                  {...register('smsUsercode')}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>
                  {t('organizations.smsPassword')}
                </label>
                <input
                  type="password"
                  {...register('smsPassword')}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>
                  {t('organizations.smsMsgheader')}
                </label>
                <input
                  type="text"
                  {...register('smsMsgheader')}
                  className={inputClass}
                />
                <p className={helpClass}>{t('organizations.smsMsgheaderHelp')}</p>
              </div>
            </div>
          )}
        </div>

        {/* Email Configuration */}
        <div className={cardClass}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('organizations.emailConfig')}</h2>
            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('emailEnabled')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900 dark:text-white">{t('organizations.emailEnabled')}</label>
            </div>
          </div>
          <p className={helpClass}>{t('organizations.emailConfigHelp')}</p>
          {emailEnabled && (
            <div className="space-y-4 mt-4">
              <div>
                <label className={labelClass}>
                  {t('organizations.emailProvider')}
                </label>
                <select
                  {...register('emailProvider')}
                  className={inputClass}
                >
                  <option value="resend">Resend</option>
                  <option value="aws">AWS SES</option>
                </select>
              </div>
              {emailProvider === 'resend' && (
                <div>
                  <label className={labelClass}>
                    {t('organizations.emailApiKey')}
                  </label>
                  <input
                    type="password"
                    {...register('emailApiKey')}
                    placeholder="re_..."
                    className={inputClass}
                  />
                </div>
              )}
              {emailProvider === 'aws' && (
                <>
                  <div>
                    <label className={labelClass}>
                      {t('organizations.emailAwsRegion')}
                    </label>
                    <input
                      type="text"
                      {...register('emailAwsRegion')}
                      placeholder="eu-west-1"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>
                      {t('organizations.emailAwsAccessKeyId')}
                    </label>
                    <input
                      type="password"
                      {...register('emailAwsAccessKeyId')}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>
                      {t('organizations.emailAwsSecretAccessKey')}
                    </label>
                    <input
                      type="password"
                      {...register('emailAwsSecretAccessKey')}
                      className={inputClass}
                    />
                  </div>
                </>
              )}
              <div>
                <label className={labelClass}>
                  {t('organizations.emailFromEmail')}
                </label>
                <input
                  type="email"
                  {...register('emailFromEmail')}
                  placeholder="noreply@yourdomain.com"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>
                  {t('organizations.emailFromName')}
                </label>
                <input
                  type="text"
                  {...register('emailFromName')}
                  placeholder="My Company Transfers"
                  className={inputClass}
                />
              </div>
            </div>
          )}
        </div>

        {/* Guest Email Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Guest E-posta Ayarları</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Transfer oluşturulduğunda guest'lere otomatik gönderilecek onay e-postası ayarları.</p>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Gönderen İsim
              </label>
              <input
                type="text"
                {...register('guestEmailSenderName')}
                placeholder="Said"
                className={inputClass}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Mailin içinde "My name is ..." olarak görünür.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Meeting Point Linki
              </label>
              <input
                type="text"
                {...register('guestEmailMeetingPoint')}
                placeholder="https://wa.me/..."
                className={inputClass}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Mailin sonunda meeting point olarak gösterilir.</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/app/organizations')}
            className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100 transition-colors min-h-[44px] font-medium"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors disabled:bg-blue-400 min-h-[44px] font-medium"
          >
            {isSubmitting
              ? 'Kaydediliyor...'
              : isEditMode
              ? 'Güncelle'
              : 'Oluştur'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OrganizationFormPage;
